import { z } from 'zod';
import {
  posDocumentScanPolicySchema,
  posProductSearchResultSchema,
  posProductSerialLookupSchema,
  posProductSupplierSkuSchema,
} from './catalog-responses';

export { posDocumentScanPolicySchema, type PosDocumentScanPolicy } from './catalog-responses';

/** Canonical physical-unit identity retained as document counting metadata. */
export const posDocumentScannedUnitSchema = z.object({
  unit_id: z.string().min(1),
  serial_id: z.string().min(1),
  value: z.string().min(1),
}).strict();
export type PosDocumentScannedUnit = z.infer<typeof posDocumentScannedUnitSchema>;

/** Fully resolved scanner selection, shared by document entry surfaces. */
export const posDocumentScannerResolutionSchema = z.object({
  product: posProductSearchResultSchema,
  serial: posProductSerialLookupSchema.optional(),
  mode: z.enum(['unique', 'non_unique', 'none']),
  mapping: posProductSupplierSkuSchema.optional(),
  supplier: z.object({
    id: z.string(),
    name: z.string(),
  }).strict().optional(),
  intent: z.enum(['scan', 'pick']),
  code: z.string().optional(),
  /** How catalog identity was obtained. Text choices require a separately
   * confirmed EAN in procurement policies; exact codes and physical serials do not. */
  selection_source: z.enum(['exact_code', 'text']).optional(),
  confirmed_ean: z.string().min(1).optional(),
  document_policy: posDocumentScanPolicySchema.optional(),
  selectionIntent: z.enum(['add', 'replace']),
}).strict();
export type PosDocumentScannerResolution = z.infer<typeof posDocumentScannerResolutionSchema>;

const pendingQuantitySchema = z.object({
  kind: z.literal('quantity'),
  resolution: posDocumentScannerResolutionSchema,
  quantityText: z.string(),
}).strict();

const pendingSerialsSchema = z.object({
  kind: z.literal('serials'),
  resolution: posDocumentScannerResolutionSchema,
  serials: z.array(posProductSerialLookupSchema),
  single: z.boolean(),
}).strict();

/** Persistable scanner workflow state. New documents start in fast mode. */
export const posDocumentScannerStateSchema = z.object({
  fast: z.boolean(),
  pending: z.discriminatedUnion('kind', [
    pendingQuantitySchema,
    pendingSerialsSchema,
  ]).nullable(),
  /** Read-only archive for PO drafts created by the retired serial collector. */
  legacy_order_serials: pendingSerialsSchema.optional(),
}).strict();
export type PosDocumentScannerState = z.infer<typeof posDocumentScannerStateSchema>;

export const defaultPosDocumentScannerState: PosDocumentScannerState = {
  fast: true,
  pending: null,
};